// StepUpModal — verification again before a tenant switch completes (task 387.11.5 api, 387.11.6 web; ADR-063).
//
// Raised when the target tenant does not accept the method that verified this session (or the session was never
// verified). The current tenant stays active until the challenge is answered; the answer returns the switch itself.

import React, { useMemo } from 'react';
import { Modal, Typography } from 'antd';
import type { MfaStep } from '@/api/contracts/session';
import type { SwitchTenantResponse } from '@/api/endpoints/auth';
import { myMfa, signInMfa } from '@/api/endpoints/mfa';
import MfaVerifyPanel, { type MfaVerifyActions } from './MfaVerifyPanel';

interface Props {
  step: MfaStep | null;
  tenantName?: string;
  /** Why verification is being asked for. Defaults to the tenant-switch wording (task 402.11). */
  prompt?: React.ReactNode;
  onSwitched: (result: SwitchTenantResponse) => void;
  onCancel: () => void;
}

function StepUpModal({
  step,
  tenantName,
  prompt,
  onSwitched,
  onCancel,
}: Props): React.ReactElement {
  const actions = useMemo<MfaVerifyActions | null>(
    () =>
      step
        ? {
            verify: async (answer) => onSwitched(await myMfa.stepUp(step.challengeToken, answer)),
            // The anonymous challenge endpoints accept a step-up token too.
            sendEmailCode: (factorId) => signInMfa.sendEmailCode(step.challengeToken, factorId),
            passkeyOptions: () => signInMfa.passkeyOptions(step.challengeToken),
          }
        : null,
    [step, onSwitched],
  );

  return (
    <Modal
      title="Confirm it is you"
      open={step !== null}
      onCancel={onCancel}
      footer={null}
      destroyOnHidden
      maskClosable={false}
    >
      {step && actions && (
        <>
          <Typography.Paragraph type="secondary">
            {prompt ??
              `${tenantName ?? 'This tenant'} asks for two-step verification before you switch.`}
          </Typography.Paragraph>
          <MfaVerifyPanel step={step} actions={actions} />
        </>
      )}
    </Modal>
  );
}

export default StepUpModal;
